<i18n>
{
  "fr": {
    "breadcrumb": "Questionnaire ESG",
    "title": "Paramétrage du questionnaire ESG",
    "no-data": "Le questionnaire n'existe pas encore. Veuillez importer votre fichier initial.",
    "export": "Exporter",
    "import": "Importer",
    "import-successful": "Le questionnaire a bien été mis à jour",
    "title-hidden": "Ce titre ne sera pas affiché dans le fichier PDF",
    "question-count": "{count, plural, 0 {aucune question} 1 {1 question} other {# questions}}",
    "error-count": "{count, plural, 1 {1 erreur a été détectée} other {# erreurs ont été détectées}}. Veuillez {count, plural, 1 {la} other {les}} corriger, puis ré-importez votre fichier.",
    "worksheetColumn": "Feuille",
    "rowNumberColumn": "# ligne",
    "errorMessageColumn": "Message d'erreur",
    "worksheet": {
      "GROUPINGS": "Regroupements",
      "QUESTIONS": "Questions"
    },
    "error": {
      "GROUPING_DUPLICATE_ID": "L'ID du regroupement est dupliqué",
      "GROUPING_EMPTY": "Le regroupement est vide",
      "GROUPING_ENGLISH_NAME_REQUIRED": "Le nom anglais est vide",
      "GROUPING_ID_REQUIRED": "L'ID du regroupement est vide",
      "GROUPING_INVALID_STRUCTURE": "La structure de regroupements est invalide",
      "GROUPING_LEVEL_MISCONFIGURED": "Un regroupement doit avoir une seule valeur parmi les colonnes Section, Étape et Groupe",
      "GROUPING_NO_QUESTION": "Aucune question n'est associée à ce groupe",
      "GROUPING_HIDDEN_YES_OR_NO": "La colonne «\u00A0Masquer le titre\u00A0» doit contenir la valeur «\u00A0Oui\u00A0» ou «\u00A0Non\u00A0».",
      "QUESTION_DUPLICATE_ID": "L'ID de la question est dupliqué",
      "QUESTION_ENGLISH_LABEL_REQUIRED": "Le libellé anglais est requis",
      "QUESTION_FRENCH_LABEL_REQUIRED": "Le libellé français est requis",
      "QUESTION_GROUP_ID_REQUIRED": "L'ID du groupe est requis",
      "QUESTION_GROUP_ID_UNKNOWN": "L'ID spécifié ne correspond pas à un groupe (niveau 3)",
      "QUESTION_ID_REQUIRED": "L'ID de la question est requis",
      "QUESTION_INDICATOR_UNKNOWN": "L'indicateur spécifié est inconnu",
      "QUESTION_TYPE_INVALID": "Le type de question est invalide"
    },
    "error-reading-file": "Une erreur est survenue lors de la lecture du fichier. Assurez-vous de choisir un fichier Excel valide au format XLSX.",
    "error-reading-data": "Une erreur est survenue lors de la lecture des données. Assurez-vous de respecter le format de données attendu."
  },
  "en": {
    "breadcrumb": "Questionnaire ESG",
    "title": "ESG questionnaire configuration",
    "no-data": "The questionnaire does not exist yet. Please import your initial file.",
    "export": "Export",
    "import": "Import",
    "import-successful": "The questionnaire has been successfully updated",
    "title-hidden": "This title will not be shown in the PDF file",
    "question-count": "{count, plural, 0 {no question} 1 {1 question} other {# questions}}",
    "error-count": "{count, plural, 1 {1 error has} other {# errors have}} been detected. Please fix {count, plural, 1 {it} other {them}}, and re-import your file.",
    "worksheetColumn": "Worksheet",
    "rowNumberColumn": "Row #",
    "errorMessageColumn": "Error message",
    "worksheet": {
      "GROUPINGS": "Groupings",
      "QUESTIONS": "Questions"
    },
    "error": {
      "GROUPING_DUPLICATE_ID": "Duplicated grouping ID",
      "GROUPING_EMPTY": "Empty grouping",
      "GROUPING_ENGLISH_NAME_REQUIRED": "The english name is required",
      "GROUPING_ID_REQUIRED": "The grouping ID is required",
      "GROUPING_INVALID_STRUCTURE": "The grouping structure is invalid",
      "GROUPING_LEVEL_MISCONFIGURED": "A grouping must have only one value between the Section, Step and Group columns",
      "GROUPING_NO_QUESTION": "No question is linked to this group",
      "GROUPING_HIDDEN_YES_OR_NO": "The \"Masquer le titre\" column must be either \"Oui\" or \"Non\".",
      "QUESTION_DUPLICATE_ID": "Duplicated question ID",
      "QUESTION_ENGLISH_LABEL_REQUIRED": "The english label is required",
      "QUESTION_FRENCH_LABEL_REQUIRED": "The french label is required",
      "QUESTION_GROUP_ID_REQUIRED": "The group ID is required",
      "QUESTION_GROUP_ID_UNKNOWN": "The specified ID does not match a group (level 3)",
      "QUESTION_ID_REQUIRED": "The question ID is required",
      "QUESTION_INDICATOR_UNKNOWN": "The specified indicator is unknown",
      "QUESTION_TYPE_INVALID": "The question type is invalid"
    },
    "error-reading-file": "An error occurred while reading the file. Make sure to select a valid Excel document, in XLSX format.",
    "error-reading-data": "An error occurred while reading des données. Make sure your data fits the expected format."
  }
}
</i18n>

<template>
  <div>
    <div
      :class="{
        'mb-24px': $media.mobile,
        'mb-32px': $media.tablet
      }"
    >
      <h1 class="title is-2 has-text-weight-bold">{{ $t('title') }}</h1>
      <div class="is-flex">
        <b-button type="is-primary" class="mr-2" @click="exportStructure">
          <b-icon icon="file-download" class="mr-1" />
          {{ $t('export') }}
        </b-button>

        <b-field class="file is-primary">
          <b-upload
            class="file-label"
            @input="importStructure"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            v-model="selectedFile"
          >
            <span class="file-cta">
              <b-icon icon="file-upload" class="mr-1" />
              {{ $t('import') }}
            </span>
          </b-upload>
        </b-field>
      </div>
    </div>

    <div class="card mb-4" v-if="errors && errors.length > 0">
      <div class="card-header">
        <p class="card-header-title has-text-danger">{{ $t('error-count', { count: errors.length }) }}</p>
      </div>
      <div class="card-content">
        <b-table :data="errors" :paginated="errors.length > 10" pagination-simple per-page="10" pagination-size="is-small">
          <b-table-column
            :label="$t('worksheetColumn')"
            v-slot="{ row }"
            header-class="has-text-danger is-no-wrap"
            cell-class="has-text-danger"
          >
            {{ $t(`worksheet.${row.sheet}`) }}
          </b-table-column>
          <b-table-column
            :label="$t('rowNumberColumn')"
            v-slot="{ row }"
            header-class="has-text-danger is-no-wrap"
            cell-class="has-text-danger"
          >
            {{ row.rowNumber }}
          </b-table-column>
          <b-table-column
            :label="$t('errorMessageColumn')"
            v-slot="{ row }"
            header-class="has-text-danger is-no-wrap"
            cell-class="has-text-danger"
            width="100%"
          >
            {{ $t(`error.${row.errorType}`) }}
          </b-table-column>
        </b-table>
      </div>
    </div>

    <div class="card">
      <b-table :data="tableData" :show-header="false">
        <template #empty>
          <div class="has-text-centered">{{ $t('no-data') }}</div>
        </template>

        <b-table-column v-slot="{ row }">
          <span :class="`level-${row.level}`">{{ row.name }}</span>
          <b-tooltip :label="$t('title-hidden')" type="is-light">
            <b-icon v-if="row.hidden" icon="eye-slash" class="has-text-grey ml-2"></b-icon>
          </b-tooltip>
        </b-table-column>
        <b-table-column v-slot="{ row }" cell-class="has-text-right">
          <span :class="`level-${row.level}`">{{ $t('question-count', { count: row.count }) }}</span>
        </b-table-column>
      </b-table>
    </div>
  </div>
</template>

<script>
import Breadcrumbs from '@/mixins/breadcrumbs'
import i18n from '@/helpers/i18n'
import { uploadTempFile } from '@/services/file-upload'

export default {
  mixins: [Breadcrumbs],
  data() {
    return {
      selectedFile: null,
      errors: null
    }
  },
  computed: {
    breadcrumbs() {
      return [
        {
          label: this.$t('breadcrumb')
        }
      ]
    },
    tableData() {
      if (!this.questionnaire) return []

      const results = []

      for (const section of this.questionnaire.sections) {
        results.push({
          level: 0,
          name: section.name,
          hidden: section.hideTitleInExport,
          count: section.steps.flatMap((x) => x.groups).flatMap((x) => x.questions).length
        })

        for (const step of section.steps) {
          results.push({
            level: 1,
            name: step.name,
            hidden: step.hideTitleInExport,
            count: step.groups.flatMap((x) => x.questions).length
          })

          for (const group of step.groups) {
            results.push({
              level: 2,
              name: group.name,
              hidden: group.hideTitleInExport,
              count: group.questions.length
            })
          }
        }
      }

      return results
    }
  },
  methods: {
    async exportStructure() {
      const { data } = await this.$apollo.mutate({
        mutation: this.$options.mutation.ExportStructure
      })

      window.open(data.exportQuestionnaireStructure.excelUrl)
    },
    async importStructure(file) {
      try {
        const fileId = await uploadTempFile(file)

        const { data } = await this.$apollo.mutate({
          mutation: this.$withFragments(this.$options.mutation.ImportStructure, this.$options.fragments.questionnaireData),
          variables: {
            fileId,
            language: i18n.contentLanguage()
          }
        })

        const { errors, isSuccess } = data.importQuestionnaireStructure

        this.errors = errors

        if (isSuccess) {
          this.$success(this.$t('import-successful'))
        }
      } finally {
        this.selectedFile = null // Clear selected file from b-upload. Allows re-uploading the same file
      }
    }
  },
  apollo: {
    questionnaire: {
      query() {
        return this.$withFragments(this.$options.query.QuestionnaireSummary, this.$options.fragments.questionnaireData)
      },
      variables() {
        return {
          language: i18n.contentLanguage()
        }
      }
    }
  },
  gqlErrors() {
    return {
      ERROR_READING_FILE: this.$t('error-reading-file'),
      ERROR_READING_DATA: this.$t('error-reading-data')
    }
  }
}
</script>

<style lang="scss" scoped>
.level-0 {
  font-size: 120%;
}
.level-1 {
  font-weight: bold;
  margin-left: 15px;
}
.level-2 {
  margin-left: 30px;
}

::v-deep {
  .is-no-wrap {
    white-space: nowrap;
  }
}
</style>

<graphql>
query QuestionnaireSummary($language: ContentLanguage!) {
  questionnaire(language: $language) {
    ...questionnaireData
  }
}

mutation ExportStructure {
  exportQuestionnaireStructure {
    excelUrl
  }
}

mutation ImportStructure($fileId: String!, $language: ContentLanguage!) {
  importQuestionnaireStructure(input: { fileId: $fileId }) {
    isSuccess
    errors {
      sheet
      rowNumber
      errorType
    }
    questionnaire(language: $language) {
      ...questionnaireData
    }
  }
}
</graphql>
<fragments>
fragment questionnaireData on QuestionnaireGraphType {
  id
  sections {
    id
    name
    hideTitleInExport
    steps {
      id
      name
      hideTitleInExport
      groups {
        id
        name
        hideTitleInExport
        questions {
          id
        }
      }
    }
  }
}
</fragments>